import { useState, useEffect, useRef, useCallback } from "react";
import * as THREE from "three";

// ── Module-level pending video registry ──
// Videos that failed autoplay register here. On the first user interaction
// in the room, PlayerScene calls playAllPendingVideos() which retries .play()
// within the user-gesture call stack (required by iOS, even in Low Power Mode).
const pendingEntries = new Set();

/**
 * Call from a user-gesture event handler (click/touchstart) to retry
 * all videos that failed autoplay. Must be synchronous within the gesture.
 */
export function playAllPendingVideos() {
  pendingEntries.forEach((entry) => {
    entry.retry();
  });
}

// ── First-frame capture (pattern from RoomThumbnail3D.js:284-395) ──
function captureFirstFrame(video) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const doCapture = () => {
      try {
        canvas.width = video.videoWidth || 256;
        canvas.height = video.videoHeight || 256;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const tex = new THREE.CanvasTexture(canvas);
        tex.needsUpdate = true;
        resolve(tex);
      } catch (err) {
        reject(err);
      }
    };

    // If video already has data, capture immediately
    if (video.readyState >= 2) {
      doCapture();
      return;
    }

    // Otherwise wait for data to load, then seek to 0.1s for a representative frame
    video.onseeked = () => doCapture();
    video.onloadeddata = () => {
      video.currentTime = 0.1;
    };
    video.oncanplay = () => {
      // Give onseeked a chance first
      setTimeout(() => {
        if (video.readyState >= 2) doCapture();
      }, 200);
    };
    video.onerror = () => {
      // MEDIA_ERR_ABORTED (code 1) fires on iOS when load() is called after a
      // rejected play() — this is recoverable, so ignore it and wait for data.
      if (video.error && video.error.code === 1) return;
      reject(new Error("Video load error"));
    };
    video.load();
  });
}

// ── Custom hook: video with autoplay fallback ──
// States: "loading" | "playing" | "static" | "error"
function useVideoWithFallback(src) {
  const [state, setState] = useState("loading");
  const [texture, setTexture] = useState(null);
  const videoRef = useRef(null);
  const textureRef = useRef(null);
  const entryRef = useRef(null);
  const mountedRef = useRef(true);

  // Retry callback — called from playAllPendingVideos() within a user gesture
  const retry = useCallback(() => {
    const video = videoRef.current;
    if (!video || !mountedRef.current) return;

    video.muted = true;
    const p = video.play();
    if (p) {
      p.then(() => {
        if (!mountedRef.current) return;
        const videoTex = new THREE.VideoTexture(video);
        // Dispose old canvas texture
        if (textureRef.current) textureRef.current.dispose();
        textureRef.current = videoTex;
        setTexture(videoTex);
        setState("playing");
        // Remove from pending set
        if (entryRef.current) {
          pendingEntries.delete(entryRef.current);
          entryRef.current = null;
        }
      }).catch(() => {
        // Still can't play even with gesture — keep static frame
      });
    }
  }, []);

  useEffect(() => {
    if (!src) return;
    mountedRef.current = true;

    const video = document.createElement("video");
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    video.crossOrigin = "anonymous";
    video.preload = "auto";
    video.src = src;
    videoRef.current = video;

    // Try autoplay
    const playPromise = video.play();

    if (playPromise) {
      playPromise
        .then(() => {
          if (!mountedRef.current) return;
          const videoTex = new THREE.VideoTexture(video);
          textureRef.current = videoTex;
          setTexture(videoTex);
          setState("playing");
        })
        .catch((playErr) => {
          if (!mountedRef.current) return;
          // Autoplay blocked — capture first frame and register for retry
          captureFirstFrame(video)
            .then((frameTex) => {
              if (!mountedRef.current) {
                frameTex.dispose();
                return;
              }
              textureRef.current = frameTex;
              setTexture(frameTex);
              setState("static");

              // Register in pending set for first-interaction retry
              const entry = { retry };
              entryRef.current = entry;
              pendingEntries.add(entry);
            })
            .catch((frameErr) => {
              if (!mountedRef.current) return;
              console.error('[VideoMaterial] Failed to load video. play error:', playErr, '| frame capture error:', frameErr, '| mediaError code:', video.error?.code, '| src:', video.src?.substring(0, 120));
              setState("error");
            });
        });
    }

    return () => {
      mountedRef.current = false;
      video.pause();
      video.removeAttribute("src");
      video.load();
      if (textureRef.current) {
        textureRef.current.dispose();
        textureRef.current = null;
      }
      if (entryRef.current) {
        pendingEntries.delete(entryRef.current);
        entryRef.current = null;
      }
    };
  }, [src, retry]);

  return { state, texture };
}

// ── Components ──

const FallbackMaterial = ({ side }) => (
  <meshStandardMaterial color="#1a1a1a" side={side} toneMapped={false} />
);

const VideoMaterialInner = ({ src, side }) => {
  const { state, texture } = useVideoWithFallback(src);

  if (state === "loading" || state === "error" || !texture) {
    return <FallbackMaterial side={side} />;
  }

  return <meshStandardMaterial map={texture} side={side} />;
};

const VideoMaterial = ({ src, side }) => {
  if (!src) {
    console.warn('[VideoMaterial] No src — decoration option is empty/undefined');
    return <FallbackMaterial side={side} />;
  }
  return <VideoMaterialInner src={src} side={side} />;
};

export default VideoMaterial;
